import { useCallback, useEffect, useState } from "react";

export interface AuthState {
  user: { id: string; name: string | null } | null;
  isEditor: boolean;
  csrfToken: string | null;
}
export function useAuth() {
  const [state, setState] = useState<AuthState>({ user: null, isEditor: false, csrfToken: null });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const refresh = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch("/api/auth/user", { credentials: "include", cache: "no-store" });
      if (!response.ok) throw new Error("Could not load your sign-in status");
      setState(await response.json());
    } catch (err) { setError(err instanceof Error ? err.message : "Could not load sign-in status"); }
    finally { setIsLoading(false); }
  }, []);
  useEffect(() => { void refresh(); }, [refresh]);
  const login = useCallback(() => {
    window.location.assign(`/api/login?returnTo=${encodeURIComponent(window.location.pathname)}`);
  }, []);
  const logout = useCallback(async () => {
    setError(null);
    try {
      const response = await fetch("/api/logout", {
        method: "POST", credentials: "include", headers: { "X-CSRF-Token": state.csrfToken ?? "" },
      });
      if (!response.ok) throw new Error("Could not sign out. Refresh and try again.");
      await refresh();
    } catch (err) { setError(err instanceof Error ? err.message : "Could not sign out"); }
  }, [state.csrfToken, refresh]);
  return { ...state, isLoading, error, isAuthenticated: !!state.user, login, logout, refresh };
}